import { ChangeDetectorRef, Component, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { BaseComponent } from '../../../core/base.component';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { HeaderService } from '../../../core/services/header.service';
import { Title } from '@angular/platform-browser';
import { NotificationService } from '../../../notification/notification.service';
import { AuthService } from '../../../core/security/auth.service';
import { RolesEnum } from '../../../core/security/roles.enum';
import {
  CurrentUser,
  E911DocumentModel,
  LookupModel,
  PbxAccountDevicesListModel,
  PbxAccountModel,
  PbxAccountVoicemailGreeting,
} from '../../../api/models';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { LocalPbxService } from '../../local-pbx.service';
import { PhoneNumbersUtils } from '../../../core/utilities/phone-numbers-helpers';
import { RequestE911AddressComponent } from './request-e911-address.component';
import { DeviceBaseComponent } from './device-buttons-settings-edit-dialog/devices/device-base/device-base.component';
import { BehaviorSubject, take, finalize, Subscription } from 'rxjs';
import { MatCard, MatCardTitle, MatCardContent } from '@angular/material/card';
import { NgIf, AsyncPipe, CommonModule, NgFor } from '@angular/common';
import { MatTextInputComponent } from '../../../editors/mat-text-input/mat-text-input.component';
import { Message } from 'primeng/message';
import { MatMultiselectComponent } from '../../../editors/mat-multiselect-input/mat-multiselect.component';
import { MatSimpleSelectComponent } from '../../../editors/mat-simple-select/mat-simple-select.component';
import { MatCheckboxInputComponent } from '../../../editors/mat-checkbox-input/mat-checkbox-input.component';
import { MatPasswordInputComponent } from '../../../editors/mat-password-input/mat-password-input.component';
import {
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { Angular2PromiseButtonModule } from 'angular2-promise-buttons';
import { MacAddressPipe } from '../../../core/pipes/mac-address.pipe';
import { SelectAudioFileDialogComponent } from '../../../dialogs/select-audio-file-dialog/select-audio-file-dialog.component';
import { AudioControlComponent } from '../../../core/visual/audio-control/audio-control.component';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatConfirmDialogComponent } from '../../../dialogs/mat-confirm/mat-confirm-dialog.component';
import { MatLabel } from '@angular/material/form-field';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { ExplanationComponent } from '../../../core/visual/explanation/explanation.component';
import { E911SignDocumentDialogComponent } from './e911-address-sign-document-dialog/e911-sign-document-dialog.component';
import { capitalize } from 'lodash';
import { DeviceVendor } from './device-buttons-settings-edit-dialog/enums';
import {
  POLYCOM_PHONE_COMPONENTS,
  YEALINK_PHONE_COMPONENTS,
} from './device-buttons-settings-edit-dialog/constants/phone-components';

@Component({
  selector: 'app-edit-pbx-account',
  templateUrl: './edit-pbx-account.component.html',
  styleUrls: ['./edit-pbx-account.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatCard,
    MatCardTitle,
    MatCardContent,
    NgIf,
    NgFor,
    MatTextInputComponent,
    Message,
    MatMultiselectComponent,
    MatSimpleSelectComponent,
    MatCheckboxInputComponent,
    MatPasswordInputComponent,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatTooltip,
    MatButton,
    MatIcon,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    Angular2PromiseButtonModule,
    AsyncPipe,
    MacAddressPipe,
    AudioControlComponent,
    MatIconButton,
    FlexLayoutModule,
    MatLabel,
    MatRadioButton,
    MatRadioGroup,
    ExplanationComponent,
    CommonModule,
  ],
})
export class EditPbxAccountComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {
  public readonly capitalize = capitalize;

  form: UntypedFormGroup;
  submitted: boolean;
  serverError: boolean;
  editModel: PbxAccountModel = {
    id: null,
    accountType: 'extensions',
    firstName: '',
    fullName: '',
    displayName: '',
    name: '',
  };

  voicemailGreetingTitles: Map<string, string> = new Map();
  voicemailGreetings: PbxAccountVoicemailGreeting[] = [];

  currentAudioSource;

  private id: string;

  e911Addresses: LookupModel[] = [];
  e911Documents: E911DocumentModel[] = [];
  accountDevicesList: PbxAccountDevicesListModel[] = [];
  displayedColumns: string[] = ['mac', 'vendor', 'model', 'actions'];
  displayedE911Columns: string[] = ['name', 'status', 'actions'];

  buttonSubmitPromise: Promise<void>;

  cellConnExplanation = `
  If call forwarding to a cell number is enabled:<br><br>
  1. When answering an incoming call on your cell, you’ll hear the following announcement: "A call is coming from the PBX, press 1 to accept."<br><br>
  2. If you don't press 1 to accept the call, the call will be forwarded to your PBX voicemail
  `;

  public readonly isButtonsAvailable$ = new BehaviorSubject<boolean>(false);

  private readonly _subscriptions = new Subscription();

  constructor(
    route: ActivatedRoute,
    headerService: HeaderService,
    titleService: Title,
    authService: AuthService,
    public dialog: MatDialog,
    public pbxService: LocalPbxService,
    private fb: UntypedFormBuilder,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
  ) {
    super(route, headerService, titleService, authService);

    this._subscriptions.add(this.subscribeParams());
    this._subscriptions.add(this.subscribeE911DocumentEvents());

    this.headerService.showBackButton(true);
    this.createForm();
  }

  get selectedVoicemailGreetingControl(): UntypedFormControl {
    return this.form.get('selectedVoicemailGreeting') as UntypedFormControl;
  }

  onCurrentUserChanged(user: CurrentUser) {
    super.onCurrentUserChanged(user);

    if (user) {
      this.loadAccountDevicesList(user);

      this.hasEditAccess = this.authService.hasAccess([
        RolesEnum.PbxAccounts_Edit,
        RolesEnum.PbxAccounts_EditOwn,
        RolesEnum.PbxAccounts_Delete,
      ]);
    } else {
      this.hasEditAccess = false;
    }
  }

  createForm(): void {
    const controlsConfig: any = {
      extension: ['', []],
      firstName: ['', Validators.required],
      lastName: ['', []],
      cellDis: [[], []],
      cellConn: [false, []],
      vpaEnabled: [false, []],
      eAni: ['', []],
      cellTime: ['', []],
      cellHunt: ['', []],
      cellAcd: ['', []],
      mbEnable: ['', []],
      mbTimeout: ['', []],
      mailboxGroup: [[], []],
      emailAddress: ['', Validators.email],
      mbPin: ['', [Validators.pattern('^(notChanged)$|^[0-9]*$')]],
      // 'mbPinConfirm': ['', []],
      emailVmail: ['none', []],
      vmailOption: ['keep', []],
      emailMissed: [false, []],
      emailAll: [false, []],
      emailMbFull: [false, []],
      cfa: [[], []],
      cfaNumbers: ['', []],
      cfaTimes: ['', []],
      cfb: [[], []],
      cfbNumbers: ['', []],
      cfbTimes: ['', []],
      cfn: [[], []],
      cfnNumbers: ['', []],
      cfnTimes: ['', []],
      cfd: [[], []],
      selectedVoicemailGreeting: ['', []],
    };

    this.form = this.fb.group(controlsConfig, {
      //validators: matchFields('mbPin', 'mbPinConfirm')
    });
  }

  async onSubmit() {
    this.serverError = false;

    if (this.form.valid && this.hasEditAccess) {
      this.buttonSubmitPromise = this.processSubmit();
    } else {
      this.notificationService.notifyDanger('Form is not valid');
    }
  }

  async processSubmit(): Promise<void> {
    try {
      this.submitted = true;

      const reqData = this.prepareFormModel();
      this.editModel = await this.pbxService.savePbxAccount(reqData);
      this.notificationService.notifySuccess('Extension updated successfully');

      this.ngOnChanges();
      //this.headerService.goBack();
    } catch (e) {
      this.serverError = true;
    } finally {
      this.submitted = false;
    }
  }

  prepareFormModel(): PbxAccountModel {
    const formModel = this.form.value;

    const resultModel: PbxAccountModel = {
      id: this.editModel.id,
      accountType: this.editModel.accountType,
      firstName: formModel.firstName,
      displayName: formModel.lastName,
      cellDis: formModel.cellDis.map(x => PhoneNumbersUtils.phoneNumberToNational(x)),
      cellConn: formModel.cellConn,
      eAni: formModel.eAni != '-1' ? formModel.eAni : '',
      noVpa: !formModel.vpaEnabled,
      cellTime: formModel.cellTime,
      cellHunt: formModel.cellHunt,
      cellAcd: formModel.cellAcd,
      mbEnable: formModel.mbEnable,
      mbTimeout: formModel.mbTimeout,
      mailboxGroup: formModel.mailboxGroup,
      emailAddress: formModel.emailAddress,
      mbPin: formModel.mbPin,
      emailVmail: formModel.emailVmail,
      vmailOption: formModel.vmailOption,
      emailMissed: formModel.emailMissed,
      emailAll: formModel.emailAll,
      emailMbFull: formModel.emailMbFull,
      cfa: formModel.cfa ? formModel.cfa[0] : '',
      cfaNumbers: formModel.cfaNumbers,
      cfaTimes: formModel.cfaTimes,
      cfb: formModel.cfb ? formModel.cfb[0] : '',
      cfbNumbers: formModel.cfbNumbers,
      cfbTimes: formModel.cfbTimes,
      cfn: formModel.cfn ? formModel.cfn[0] : '',
      cfnNumbers: formModel.cfnNumbers,
      cfnTimes: formModel.cfnTimes,
      cfd: formModel.cfd ? formModel.cfd[0] : '',
      nameUse: formModel.selectedVoicemailGreeting === 'name',
      personal:
        this.pbxService.voicemailGreetings.find(g => g.id === formModel.selectedVoicemailGreeting)?.metadata || '',
    };

    return resultModel;
  }

  async ngOnInit() {
    this.voicemailGreetingTitles = new Map(this.pbxService.voicemailGreetings.map(g => [g.id as string, g.text]));

    super.ngOnInit();

    this.loadPbxAccountById();
    this.loadPbxE911Addresses();

    await this.pbxService.getLookupLists();

    this.loadE911Documents();
  }

  ngOnChanges(changes?: SimpleChanges): void {
    const defValues: any = {
      extension: this.editModel.name,
      firstName: this.editModel.firstName,
      lastName: this.editModel.displayName,
      eAni: this.editModel.eAni,
      cellDis: this.editModel.cellDis,
      cellConn: this.editModel.cellConn,
      vpaEnabled: !this.editModel.noVpa,
      cellTime: this.editModel.cellTime,
      cellHunt: this.editModel.cellHunt,
      cellAcd: this.editModel.cellAcd,
      mbEnable: this.editModel.mbEnable,
      mbTimeout: this.editModel.mbTimeout,
      mailboxGroup: this.editModel.mailboxGroup,
      emailAddress: this.editModel.emailAddress,
      mbPin: this.editModel.mbPin,
      //'mbPinConfirm': this.editModel.mbPin,
      emailVmail: this.editModel.emailVmail,
      vmailOption: this.editModel.vmailOption,
      emailMissed: this.editModel.emailMissed,
      emailAll: this.editModel.emailAll,
      emailMbFull: this.editModel.emailMbFull,
      cfa: this.editModel.cfa ? [this.editModel.cfa] : [],
      cfaNumbers: this.editModel.cfaNumbers,
      cfaTimes: this.editModel.cfaTimes,
      cfb: this.editModel.cfb ? [this.editModel.cfb] : [],
      cfbNumbers: this.editModel.cfbNumbers,
      cfbTimes: this.editModel.cfbTimes,
      cfn: this.editModel.cfn ? [this.editModel.cfn] : [],
      cfnNumbers: this.editModel.cfnNumbers,
      cfnTimes: this.editModel.cfnTimes,
      cfd: this.editModel.cfd ? [this.editModel.cfd] : [],
      selectedVoicemailGreeting: this.getVoicemailGreetingValue(this.editModel.nameUse, this.editModel.personal),
    };

    this.form.reset(defValues);
    this.form.markAllAsTouched();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.headerService.showBackButton(false);
    this._subscriptions.unsubscribe();
  }

  /*async onDeleteClick() {
    const dialogRef = this.dialog.open(MatConfirmDialogComponent, {
      // width: '250px',
      data: {
        title: 'Delete Pbx Account',
        text: 'Are you sure you want to delete account on pbx?',
        actionTitle: 'Delete',
      }
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.onDeleteProcess();
      }
    });
  }

  async onDeleteProcess(): Promise<void> {
    await this.pbxService.deletePbxAccount(this.editModel.id);
    await this.notificationService.notifySuccess('Role deleted successfully');
    await this.headerService.goToComponent(PbxGroupsComponent.name);
  }*/

  specialMenuChanged($event: any) {
    if ($event) {
      this.form.patchValue({
        mbTimeout: '60',
      });
    }
  }

  onSelectedE911AddressChanged($event: any) {
    if ($event === '-1') {
      this.dialog
        .open(RequestE911AddressComponent, {
          disableClose: true,
          width: '500px',
          data: {
            pbxAccount: this.editModel,
          },
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe(data => {
          if (data) {
            this.e911Documents = [data, ...this.e911Documents];
            this.onSignE911Document(null, data);
          }
        });

      this.form.get('eAni')?.patchValue('');
    }
  }

  public isDeviceEditDisabled(device: PbxAccountDevicesListModel): boolean {
    return !!!device.model || !!!device.vendor;
  }

  public onSignE911Document($event, document: E911DocumentModel): void {
    $event?.preventDefault();

    this.dialog.open(E911SignDocumentDialogComponent, {
      disableClose: true,
      width: '1100px',
      height: '90%',
      data: {
        document,
        pbxAccountId: this.editModel?.id,
      },
    });
  }

  public onEditVoicemailGreeting(greeting: string): void {
    this.dialog
      .open(SelectAudioFileDialogComponent, {
        id: 'holiday-greetings-edit-recording-dialog',
        width: '550px',
        height: '90%',
        data: {
          availableFiles: [
            {
              id: '-1',
              name: 'Custom Audio File',
              cloudUrl: '',
              fileName: '',
              fileGroup: 'Custom Audio File',
            },
            {
              id: '-2',
              name: 'Text To Speech',
              cloudUrl: '',
              fileName: '',
              fileGroup: 'Voicemail Greeting By Text To Audio',
            },
          ],
          recId: null,
          title: `Select Voicemail Greeting - ${this.voicemailGreetingTitles.get(greeting)?.split(' ')?.[0]}`,
        },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe(data => {
        if (data) {
          this.uploadVoicemailGreeting(greeting, data.file);
        }
      });
  }

  public onPlayVoicemailGreeting(greeting: string): void {
    this.currentAudioSource = `/api/pbx-account-voicemail-greetings/pre-audio/stream/${this.editModel.name}/${greeting}/?t=${new Date().getTime()}`;
  }

  public onDeleteVoicemailGreeting(greeting: string): void {
    this.dialog
      .open(MatConfirmDialogComponent, {
        width: '400px',
        data: {
          title: 'Delete Voicemail Greeting?',
          text: 'Are you sure you want to delete voicemail greeting?',
          actionTitle: 'Delete',
        },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe(confirm => {
        if (confirm) {
          this.uploadVoicemailGreeting(greeting);
        }
      });
  }

  public onEditDeviceButtonsSettings(device: PbxAccountDevicesListModel): void {
    if (this.isDeviceEditDisabled(device)) {
      return;
    }

    const selectedComponent = this.getComponentByVendorAndModel(device?.vendor, device?.model);
    const dialogConfig: MatDialogConfig = {
      width: '90%',
      height: '90%',
      data: { deviceId: device.id, extension: this.editModel },
    };

    if (!selectedComponent) {
      this.dialog.open(DeviceBaseComponent, { ...dialogConfig, data: { ...dialogConfig.data, isDefault: true } });

      return;
    }

    this.dialog.open(selectedComponent, dialogConfig);
  }

  private getComponentByVendorAndModel(vendor: string, model: string): any {
    if (!vendor || !model) {
      return null;
    }

    switch (vendor) {
      case DeviceVendor.Yealink:
        return YEALINK_PHONE_COMPONENTS[model.slice(0, 3)];
      case DeviceVendor.Polycom:
      case DeviceVendor.Poly:
        return POLYCOM_PHONE_COMPONENTS[model];
      default:
        return null;
    }
  }

  private getVoicemailGreetingValue(nameUse: boolean, personal: string): string {
    if (!nameUse && !personal) {
      return 'anonymous';
    }

    //NOTE: returning empty string if no audio forcing user to select valid greeting option

    if (nameUse) {
      return this.hasVoicemailGreeting('name') ? 'name' : '';
    }

    return this.hasVoicemailGreeting(`personal${personal}`) ? `personal${personal}` : '';
  }

  private hasVoicemailGreeting(id: string): boolean {
    const greeting = (this.voicemailGreetings || []).find(g => g.id === id);

    return !!greeting?.hasRecording;
  }

  private loadPbxAccountById(): void {
    this.pbxService
      .getPbxAccountById(this.id)
      .pipe(take(1))
      .subscribe({
        next: editModel => {
          this.editModel = editModel;

          if (editModel.name) {
            this.loadAvailableVoicemailGreetings(editModel.name);
          }

          this.ngOnChanges();
        },
      });
  }

  private loadPbxE911Addresses(): void {
    this.pbxService
      .getPbxE911Addresses()
      .pipe(take(1))
      .subscribe(e911AddressList => {
        this.e911Addresses = e911AddressList.map(value => {
          return {
            id: value.telephoneNumber,
            text: value.fullAddress,
          };
        });
        this.e911Addresses.push({
          id: '-1',
          text: 'Request New E-911 Address',
        });
      });
  }

  private loadE911Documents(): void {
    const { name, eAni } = this.editModel || {};

    this.pbxService
      .getE911Documents({ extension: name || '', eAni: eAni.replace(/^\+/, '') || '' })
      .pipe(take(1))
      .subscribe({
        next: data => {
          this.e911Documents = data || [];
        },
      });
  }

  private loadAccountDevicesList(user: CurrentUser): void {
    const isAvailable = user.serverVersion === 'V6';

    if (isAvailable) {
      this.pbxService
        .getPbxAccountDevicesList(this.id)
        .pipe(take(1))
        .subscribe({
          next: devicesList => (this.accountDevicesList = devicesList || []),
        });
    }

    this.isButtonsAvailable$.next(isAvailable);
  }

  private uploadVoicemailGreeting(greeting: string, file: Blob = null): void {
    this.submitted = true;

    this.pbxService
      .uploadPbxAccountVoicemailGreeting(this.editModel.name, greeting, file)
      .pipe(
        take(1),
        finalize(() => {
          this.submitted = false;
        }),
      )
      .subscribe({
        next: () => {
          this.notificationService.notifySuccess(`Voicemail greeting ${file ? 'uploaded' : 'deleted'} successfully`);
          this.loadAvailableVoicemailGreetings(this.editModel.name, true);

          if (!file && this.form.get('selectedVoicemailGreeting')?.value === greeting) {
            this.selectedVoicemailGreetingControl.patchValue('');
          }

          this.currentAudioSource = null;
        },
      });
  }

  private loadAvailableVoicemailGreetings(extension: string, reload = false): void {
    this.pbxService
      .getPbxAccountVoicemailGreetings(extension)
      .pipe(take(1))
      .subscribe({
        next: data => {
          this.voicemailGreetings = [{ hasRecording: false, id: 'anonymous' }, ...data];

          if (!reload) {
            this.selectedVoicemailGreetingControl.patchValue(
              this.getVoicemailGreetingValue(this.editModel.nameUse, this.editModel.personal),
            );
          }
        },
      });
  }

  private subscribeE911DocumentEvents(): Subscription {
    return this.pbxService.e911DocumentEvents.subscribe(data => {
      const { documentId, documentStatus, accountId, e911Details } = data || {};

      const doc = this.e911Documents.find(d => d.documentId === documentId);

      if (doc) {
        doc.status = documentStatus;
      }

      if (e911Details) {
        this.e911Addresses.unshift({
          id: e911Details.telephoneNumber,
          text: e911Details.fullAddress,
        });

        this.notificationService.notifySuccess(`New E911 address added: ${e911Details.fullAddress}`);

        if (accountId === this.editModel.id) {
          this.form.get('eAni')?.patchValue(e911Details.telephoneNumber);
        }
      }
    });
  }

  private subscribeParams(): Subscription {
    return this.route.params.subscribe(params => {
      this.id = params['id'];
      console.log(this.id);
    });
  }
}
